<script setup lang="ts">
type Variant = "DEFAULT" | "POSITIVE" | "NEGATIVE";

const {
  text = undefined,
  disabled = false,
  variant = "DEFAULT",
  fullWidth = false,
  narrow = false,
} = defineProps<{
  text?: string;
  disabled?: boolean;
  variant?: Variant;
  fullWidth?: boolean;
  narrow?: boolean;
}>();

defineOptions({
  inheritAttrs: true,
});
</script>

<template>
  <button
    class="pwt-theme pwt-button--base pwt-button--solid"
    :class="{
      'pwt-rounded': fullWidth,
      'pwt-rounded-pill': !fullWidth,
      'pwt-full-width': fullWidth,
      'pwt-optimiser-blur': fullWidth && variant === 'DEFAULT' && !disabled,
      'pwt-button--solid--default': variant === 'DEFAULT',
      'pwt-button--solid--negative': variant === 'NEGATIVE',
      'pwt-button--solid--positive': variant === 'POSITIVE',
      'pwt-button--solid--narrow': narrow,
    }"
    v-bind="$attrs"
    :disabled="disabled"
  >
    <span class="pwt-text-truncate">
      <slot v-if="$slots.default"></slot>
      <template v-else>{{ text }}</template>
    </span>
  </button>
</template>

<style lang="css" scoped>
@import "./styles/pwt-theme.css";
@import "./styles/pwt-utilities.css";
@import "./styles/button-shared.css";

.pwt-button--solid {
  padding: 10px 36px;
  min-width: 100px;
  transition:
    background-color 150ms ease,
    color 150ms ease;
}

.pwt-button--solid:disabled {
  background-color: var(--platinum);
  color: var(--light-gray);
  cursor: default;
}

.pwt-button--solid--narrow {
  padding: 6px 27px 6.5px 24px;
  min-width: 0;
}

/* variants: default */
.pwt-button--solid:not(:disabled).pwt-button--solid--default {
  background-color: var(--palatinate-blue-100);
  color: var(--white-100);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--default:hover {
  background-color: var(--palatinate-blue-120);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--default:active {
  background-color: var(--palatinate-blue-140);
}

/* variants: positive */
.pwt-button--solid:not(:disabled).pwt-button--solid--positive {
  background-color: var(--mint-40);
  color: var(--green-100);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--positive:hover {
  background-color: var(--mint-60);
  color: var(--green-120);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--positive:active {
  background-color: var(--mint-80, #97f2cc);
  color: var(--green-120, #12662e);
}

/* variants: negative */
.pwt-button--solid:not(:disabled).pwt-button--solid--negative {
  background-color: var(--pink-20);
  color: var(--red-100);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--negative:hover {
  background-color: var(--pink-40);
  color: var(--red-120);
}

.pwt-button--solid:not(:disabled).pwt-button--solid--negative:active {
  background-color: var(--pink-60);
  color: var(--red-120);
}

</style>
